import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import BusinessSettingsContext from "../Context/Settings/BusinessSettingsContext";
import CampaignFunnelPanel from "./CampaignFunnelPanel";
import CampaignManagementPanel from "./CampaignManagementPanel";
import FollowUpDetails from "./FollowUpDetails";
import {
  CampaignDoc,
  CampaignMessageDoc,
  CampaignMessageStatus,
  getCampaign,
  getCampaignMessages,
  retryFailedCampaignMessages,
  reorderCampaignFollowUps,
  startCampaign,
} from "../../services/campaign.service";
import {
  CampaignFunnelState,
  addCampaignFunnelStage,
  assignCampaignLeadToStage,
  createCampaignFunnelCustomField,
  createDefaultCampaignFunnelState,
  getCampaignFunnelRecordId,
  getCampaignFunnelState,
  moveCampaignFunnelRecord,
  normalizeCampaignFunnelState,
  removeCampaignFunnelRecord,
  saveCampaignFunnelState,
  updateCampaignFunnelRecord,
} from "../../services/campaignFunnel.service";

const STATUSES: CampaignMessageStatus[] = [
  "QUEUED",
  "SENT",
  "DELIVERED",
  "READ",
  "FAILED",
];

type Props = {
  businessId: string;
  campaignId: string;
  onBack: () => void;
  onOpenCampaign: (id: string) => void;
};

type MetricTileProps = {
  label: string;
  value: number;
  subtitle: string;
  tone: "neutral" | "blue" | "green" | "red";
  icon: React.ReactNode;
};

const getCampaignMessageErrorText = (message: CampaignMessageDoc) => {
  const error = message.lastError || message.error;
  const details = error?.error_data?.details?.trim();
  const summary = error?.message?.trim() || error?.title?.trim();

  if (summary && details && summary.toLowerCase() !== details.toLowerCase()) {
    return `${summary}: ${details}`;
  }

  if (details || summary) {
    return details || summary || "-";
  }

  return message.status === "FAILED" ? "Message failed to send." : "-";
};

function MetricTile({ label, value, subtitle, tone, icon }: MetricTileProps) {
  const theme = useTheme();

  const tones = {
    neutral: {
      border: alpha(theme.palette.text.primary, 0.1),
      background: theme.palette.background.paper,
      iconBg: alpha(theme.palette.text.primary, 0.07),
      iconColor: theme.palette.text.primary,
    },
    blue: {
      border: alpha(theme.palette.primary.main, 0.18),
      background: theme.palette.background.paper,
      iconBg: alpha(theme.palette.primary.main, 0.16),
      iconColor: theme.palette.primary.main,
    },
    green: {
      border: alpha(theme.palette.success.main, 0.22),
      background: theme.palette.background.paper,
      iconBg: alpha(theme.palette.success.main, 0.18),
      iconColor: theme.palette.success.main,
    },
    red: {
      border: alpha(theme.palette.error.main, 0.2),
      background: theme.palette.background.paper,
      iconBg: alpha(theme.palette.error.main, 0.16),
      iconColor: theme.palette.error.main,
    },
  } as const;

  const palette = tones[tone];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        borderRadius: 2,
        borderColor: palette.border,
        background: palette.background,
        boxShadow: "none",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            bgcolor: palette.iconBg,
            color: palette.iconColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ letterSpacing: 0.3, opacity: 0.7 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1, mt: 0.4 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.72 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function CampaignDetails({
  businessId,
  campaignId,
  onBack,
  onOpenCampaign,
}: Props) {
  const theme = useTheme();
  const { settings } = useContext(BusinessSettingsContext);

  const uuid =
    businessId ||
    (settings as any)?.businessSettings?.businessId ||
    (settings as any)?.businessId;

  const [campaign, setCampaign] = useState<CampaignDoc | null>(null);
  const [messages, setMessages] = useState<CampaignMessageDoc[]>([]);
  const [funnelState, setFunnelState] = useState<CampaignFunnelState>(
    createDefaultCampaignFunnelState(),
  );
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelSaving, setFunnelSaving] = useState(false);
  const [funnelDirty, setFunnelDirty] = useState(false);
  const [funnelErr, setFunnelErr] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<CampaignMessageStatus | "">(
    "",
  );
  const [selectedBatchNumbers, setSelectedBatchNumbers] = useState<number[]>([]);
  const [err, setErr] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState<0 | 1>(0);
  const [selectedFollowUpId, setSelectedFollowUpId] = useState("");
  const saveCounterRef = useRef(0);

  const load = async (u: string, id: string) => {
    setLoading(true);
    setErr("");
    try {
      const c = await getCampaign(u, id);
      setCampaign(c);
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail || e?.message || "Failed to load campaign",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (u: string, id: string) => {
    setLoadingMsgs(true);
    setErr("");
    try {
      const msgs = await getCampaignMessages(
        u,
        id,
        statusFilter || undefined,
        1,
        50,
        selectedBatchNumbers,
      );
      setMessages(msgs);
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to load campaign messages",
      );
    } finally {
      setLoadingMsgs(false);
    }
  };

  const loadFunnel = async (u: string, id: string) => {
    setFunnelLoading(true);
    setFunnelErr("");
    try {
      const state = await getCampaignFunnelState(u, id);
      setFunnelState(state);
      setFunnelDirty(false);
    } catch (e: any) {
      setFunnelErr(
        e?.response?.data?.detail || e?.message || "Failed to load campaign funnel",
      );
    } finally {
      setFunnelLoading(false);
    }
  };

  useEffect(() => {
    if (!uuid || !campaignId) return;
    load(uuid, campaignId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, campaignId]);

  useEffect(() => {
    if (!uuid || !campaignId) return;
    loadMessages(uuid, campaignId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, campaignId, statusFilter, selectedBatchNumbers]);

  useEffect(() => {
    setSelectedBatchNumbers([]);
  }, [campaignId]);

  useEffect(() => {
    if (!uuid || !campaignId) {
      setFunnelState(createDefaultCampaignFunnelState());
      setFunnelDirty(false);
      return;
    }

    loadFunnel(uuid, campaignId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, campaignId]);

  useEffect(() => {
    if (!uuid || !campaignId || !funnelDirty) {
      return;
    }

    const saveId = saveCounterRef.current + 1;
    saveCounterRef.current = saveId;

    const timeoutId = window.setTimeout(async () => {
      setFunnelSaving(true);
      setFunnelErr("");
      try {
        await saveCampaignFunnelState(uuid, campaignId, funnelState);
        if (saveCounterRef.current === saveId) {
          setFunnelDirty(false);
        }
      } catch (e: any) {
        setFunnelErr(
          e?.response?.data?.detail ||
            e?.message ||
            "Failed to save campaign funnel",
        );
      } finally {
        if (saveCounterRef.current === saveId) {
          setFunnelSaving(false);
        }
      }
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [uuid, campaignId, funnelDirty, funnelState]);

  if (!uuid) return <Alert severity="warning">Loading settings...</Alert>;
  if (!campaignId) return <Alert severity="error">Campaign ID missing</Alert>;
  if (err) return <Alert severity="error">{err}</Alert>;

  if (selectedFollowUpId) {
    return (
      <FollowUpDetails
        uuid={uuid}
        campaignId={campaignId}
        followUpId={selectedFollowUpId}
        campaignName={campaign?.name || "Campaign"}
        listIds={campaign?.listIds || []}
        onBack={() => setSelectedFollowUpId("")}
        onChanged={() => load(uuid, campaignId)}
      />
    );
  }

  const m = campaign?.metrics || {};
  const sentCount = Number(m.SENT || 0);
  const deliveredCount = Number(m.DELIVERED || 0);
  const readCount = Number(m.READ || 0);
  const failedCount = Number(m.FAILED || 0);
  const queuedCount = Number(m.QUEUED || 0);
  const messagesSentCount =
    sentCount + deliveredCount + readCount;
  const campaignStatus = String(campaign?.status || "").toUpperCase();
  const canRetryQueued =
    queuedCount > 0 && ["READY", "COMPLETED", "FAILED"].includes(campaignStatus);
  const canRetryFailed = failedCount > 0 && campaignStatus === "COMPLETED";

  const retryQueuedMessages = async () => {
    if (!uuid || !campaignId) return;

    setRestarting(true);
    setErr("");
    try {
      await startCampaign(uuid, campaignId);
      await Promise.all([
        load(uuid, campaignId),
        loadMessages(uuid, campaignId),
      ]);
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to restart queued campaign messages",
      );
    } finally {
      setRestarting(false);
    }
  };

  const retryFailedMessages = async () => {
    if (!uuid || !campaignId || !canRetryFailed) return;

    setRestarting(true);
    setErr("");
    try {
      await retryFailedCampaignMessages(uuid, campaignId);
      await Promise.all([
        load(uuid, campaignId),
        loadMessages(uuid, campaignId),
      ]);
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to retry failed campaign messages",
      );
    } finally {
      setRestarting(false);
    }
  };
  const totalCampaignLeads =
    Number(m.TOTAL) ||
    new Set(messages.map((row) => getCampaignFunnelRecordId(row))).size;

  const stageCounts = funnelState.records.reduce<Record<string, number>>(
    (acc, record) => {
      acc[record.stageId] = (acc[record.stageId] || 0) + 1;
      return acc;
    },
    {},
  );

  const recordStageLookup = funnelState.records.reduce<Record<string, string>>(
    (acc, record) => {
      acc[record.id] = record.stageId;
      return acc;
    },
    {},
  );

  const interestedCount = stageCounts.interested || 0;
  const customerCount = stageCounts.customers || 0;

  const updateFunnelState = (
    updater: (current: CampaignFunnelState) => CampaignFunnelState,
  ) => {
    setFunnelState((current) => {
      const next = normalizeCampaignFunnelState(updater(current));
      setFunnelDirty(true);
      return next;
    });
  };

  const moveFollowUp = async (followUpId: string, direction: -1 | 1) => {
    const editable = (campaign?.followUpJobs || []).filter((job) =>
      ["WAITING", "SCHEDULED", "PAUSED"].includes(String(job.status).toUpperCase()),
    );
    const index = editable.findIndex((job) => job._id === followUpId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= editable.length) return;
    const ids = editable.map((job) => job._id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setRestarting(true);
    setErr("");
    try {
      await reorderCampaignFollowUps(uuid, campaignId, ids);
      await load(uuid, campaignId);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to reorder follow-ups");
    } finally {
      setRestarting(false);
    }
  };

  const handleAddStage = (stageName: string) => {
    let createdStageId: string | null = null;

    updateFunnelState((current) => {
      const output = addCampaignFunnelStage(current, stageName);
      createdStageId = output.stageId;
      return output.state;
    });

    return createdStageId;
  };

  const metricTiles: MetricTileProps[] = [
    {
      label: "Audience",
      value: Number(m.TOTAL || 0),
      subtitle: "Total numbers inside this campaign",
      tone: "neutral",
      icon: <Groups2OutlinedIcon fontSize="small" />,
    },
    {
      label: "Messages Sent",
      value: messagesSentCount,
      subtitle: "Outbound WhatsApp template sends",
      tone: "blue",
      icon: <SendRoundedIcon fontSize="small" />,
    },
    {
      label: "Read Messages",
      value: readCount,
      subtitle: "Recipients who opened the message",
      tone: "green",
      icon: <MarkEmailReadOutlinedIcon fontSize="small" />,
    },
    {
      label: "Failed Sends",
      value: failedCount,
      subtitle: "Delivery problems needing attention",
      tone: "red",
      icon: <ErrorOutlineIcon fontSize="small" />,
    },
    {
      label: "Interested Leads",
      value: interestedCount,
      subtitle: "Moved into the funnel for follow-up",
      tone: "blue",
      icon: <FavoriteBorderIcon fontSize="small" />,
    },
    {
      label: "Won Customers",
      value: customerCount,
      subtitle: "Leads converted from this campaign",
      tone: "green",
      icon: <TrackChangesOutlinedIcon fontSize="small" />,
    },
  ];

  const crmSyncLabel = funnelSaving
    ? "Saving CRM updates..."
    : funnelLoading
      ? "Loading CRM workspace..."
      : "CRM data synced to backend";

  const toggleBatch = (batchNumber: number) => {
    setSelectedBatchNumbers((current) =>
      current.includes(batchNumber)
        ? current.filter((number) => number !== batchNumber)
        : [...current, batchNumber].sort((left, right) => left - right),
    );
  };

  return (
    <Box sx={{ p: 0, display: "grid", gap: 2 }}>
      <Paper
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 2,
          color: "text.primary",
          bgcolor: "background.paper",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          boxShadow: "none",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                sx={{ borderRadius: 2.5 }}
              >
                Back
              </Button>

              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  boxShadow: "none",
                }}
              >
                <CampaignOutlinedIcon />
              </Box>

              <Box>
                <Typography
                  variant="overline"
                  sx={{ display: "block", letterSpacing: 0.5, opacity: 0.7 }}
                >
                  Campaign workspace
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                  {campaign?.name || (loading ? "Loading campaign..." : "-")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 0.75, flexWrap: "wrap", gap: 1 }}
                >
                  <Chip
                    size="small"
                    label={campaign?.status || "-"}
                    color={campaign?.status === "COMPLETED" ? "success" : "default"}
                    icon={
                      campaign?.status === "COMPLETED" ? (
                        <CheckCircleOutlineIcon />
                      ) : undefined
                    }
                    sx={{ fontWeight: 700 }}
                  />
                  {campaign?.scheduledAt && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Scheduled: ${new Date(campaign.scheduledAt).toLocaleString()}`}
                    />
                  )}
                  {campaign?.lists?.map((list) => (
                    <Chip
                      key={list.id}
                      size="small"
                      variant="outlined"
                      label={`List: ${list.name}`}
                    />
                  ))}
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Move leads into pipeline stages and track campaign-to-customer
                    conversion from one screen.
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Chip
                label={crmSyncLabel}
                color={funnelSaving ? "warning" : "default"}
                variant={funnelSaving ? "filled" : "outlined"}
                sx={{ borderRadius: 2 }}
              />
              {canRetryQueued && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    restarting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SendRoundedIcon />
                    )
                  }
                  onClick={retryQueuedMessages}
                  disabled={restarting || loadingMsgs || loading}
                  sx={{ borderRadius: 2.5, px: 2 }}
                >
                  Retry queued ({queuedCount})
                </Button>
              )}
              {failedCount > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={
                    restarting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <ReplayIcon />
                    )
                  }
                  onClick={retryFailedMessages}
                  disabled={!canRetryFailed || restarting || loadingMsgs || loading}
                  title={
                    canRetryFailed
                      ? "Retry only failed recipients"
                      : "Available after the campaign is completed"
                  }
                  sx={{ borderRadius: 2.5, px: 2 }}
                >
                  Retry failed ({failedCount})
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={
                  loadingMsgs || loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={() => {
                  if (!uuid || !campaignId) return;
                  load(uuid, campaignId);
                  loadMessages(uuid, campaignId);
                  loadFunnel(uuid, campaignId);
                }}
                disabled={loadingMsgs || loading}
                sx={{
                  borderRadius: 2.5,
                  px: 2,
                  boxShadow: "none",
                }}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          {funnelErr && <Alert severity="error">{funnelErr}</Alert>}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(6, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {metricTiles.map((tile) => (
              <MetricTile key={tile.label} {...tile} />
            ))}
          </Box>
        </Stack>
      </Paper>

      {(campaign?.deliveryBatches?.length || 0) > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="h6" fontWeight={800}>Staggered batches</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Select one or more batches to show only their contacts in Message activity.
                </Typography>
              </Box>
              <Button
                size="small"
                variant={selectedBatchNumbers.length === 0 ? "contained" : "outlined"}
                onClick={() => setSelectedBatchNumbers([])}
                sx={{ borderRadius: 999, alignSelf: { xs: "flex-start", sm: "center" } }}
              >
                All batches
              </Button>
            </Stack>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedBatchNumbers.length === 0}
                      indeterminate={selectedBatchNumbers.length > 0}
                      onChange={() => setSelectedBatchNumbers([])}
                      inputProps={{ "aria-label": "Show all batches" }}
                    />
                  </TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Scheduled</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaign?.deliveryBatches?.map((batch) => (
                  <TableRow
                    key={batch.id}
                    hover
                    selected={selectedBatchNumbers.includes(batch.number)}
                    onClick={() => toggleBatch(batch.number)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedBatchNumbers.includes(batch.number)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleBatch(batch.number)}
                        inputProps={{ "aria-label": `Select batch ${batch.number}` }}
                      />
                    </TableCell>
                    <TableCell>Batch {batch.number}</TableCell>
                    <TableCell>
                      {batch.recipientCount.toLocaleString()}
                      {Boolean(batch.failedCount) && (
                        <Typography variant="caption" color="error" sx={{ display: "block" }}>
                          {batch.failedCount} failed
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{new Date(batch.scheduledAt).toLocaleString()}</TableCell>
                    <TableCell><Chip size="small" label={batch.status} variant="outlined" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {campaign && (
        <CampaignManagementPanel
          uuid={uuid}
          campaign={campaign}
          onRefresh={() => load(uuid, campaignId)}
          onBack={onBack}
          onOpenCampaign={onOpenCampaign}
        />
      )}

      {(campaign?.followUpJobs?.length || 0) > 0 && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: alpha(theme.palette.text.primary, 0.08),
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={800}>
              Follow-up sequence
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Scheduled templates and their current delivery progress.
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Step</TableCell>
                  <TableCell>Scheduled</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Recipients</TableCell>
                    <TableCell align="right">Sent</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Controls</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaign?.followUpJobs?.map((job) => (
                  <TableRow key={job._id}>
                    <TableCell>{job.step}</TableCell>
                    <TableCell>
                      {job.scheduledAt
                        ? new Date(job.scheduledAt).toLocaleString()
                        : job.estimatedScheduledAt
                          ? `After previous step (estimated ${new Date(job.estimatedScheduledAt).toLocaleString()})`
                          : "After previous step"}
                    </TableCell>
                    <TableCell>
                      {job.audience === "INTERESTED"
                        ? "Interested only"
                        : "All recipients"}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={job.status} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{job.recipientCount ?? "-"}</TableCell>
                    <TableCell align="right">{job.sentCount ?? "-"}</TableCell>
                    <TableCell align="right">{job.failedCount ?? "-"}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {["WAITING", "SCHEDULED", "PAUSED"].includes(
                          String(job.status).toUpperCase(),
                        ) && (
                          <>
                            <Button size="small" disabled={restarting} onClick={() => moveFollowUp(job._id, -1)}>Up</Button>
                            <Button size="small" disabled={restarting} onClick={() => moveFollowUp(job._id, 1)}>Down</Button>
                          </>
                        )}
                        <Button size="small" variant="outlined" onClick={() => setSelectedFollowUpId(job._id)}>View</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {(campaign?.activity?.length || 0) > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={800}>Campaign activity history</Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {campaign?.activity?.slice().reverse().map((entry, index) => (
              <Box key={`${entry.createdAt}-${index}`}>
                <Typography variant="body2" fontWeight={700}>
                  {entry.action.replace(/_/g, " ")}
                </Typography>
                <Typography variant="caption">
                  {new Date(entry.createdAt).toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: alpha(theme.palette.text.primary, 0.08),
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={workspaceTab}
          onChange={(_, nextValue: 0 | 1) => setWorkspaceTab(nextValue)}
          variant="fullWidth"
          sx={{
            px: { xs: 1, md: 2 },
            bgcolor: "#FFFFFF",
            borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          }}
        >
          <Tab label={`Message activity (${messages.length})`} />
          <Tab label={`CRM funnel (${funnelState.records.length})`} />
        </Tabs>
      </Paper>

      {workspaceTab === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            borderColor: alpha(theme.palette.text.primary, 0.08),
            boxShadow: "none",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 2.5 },
              py: 2.25,
              borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
              bgcolor: "background.paper",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1.5}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Message activity
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.72 }}>
                  Review campaign delivery outcomes and push strong responses into
                  your CRM funnel from here.
                </Typography>
              </Box>

              <Chip
                label={`${messages.length} visible messages${selectedBatchNumbers.length ? ` from ${selectedBatchNumbers.length} batch${selectedBatchNumbers.length === 1 ? "" : "es"}` : ""}`}
                variant="outlined"
                sx={{ borderRadius: 2, fontWeight: 700 }}
              />
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
            >
              <Button
                size="small"
                variant={statusFilter === "" ? "contained" : "outlined"}
                onClick={() => setStatusFilter("")}
                sx={{ borderRadius: 999 }}
              >
                All activity
              </Button>
              {STATUSES.map((s) => (
                <Button
                  key={s}
                  size="small"
                  variant={statusFilter === s ? "contained" : "outlined"}
                  onClick={() => setStatusFilter(s)}
                  sx={{ borderRadius: 999 }}
                >
                  {s}
                </Button>
              ))}
            </Stack>
          </Box>

          {loadingMsgs ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ px: 2.5, py: 4 }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Loading message activity...
              </Typography>
            </Stack>
          ) : (
            <TableContainer sx={{ maxHeight: { xs: "unset", xl: 860 } }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700 }}>
                      Contact
                    </TableCell>
                    {(campaign?.deliveryBatches?.length || 0) > 0 && (
                      <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700 }}>
                        Batch
                      </TableCell>
                    )}
                    <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700 }}>
                      Delivery
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700 }}>
                      CRM stage
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700 }}>
                      WhatsApp message ID
                    </TableCell>
                    <TableCell sx={{ bgcolor: "#F8FAFC", fontWeight: 700 }}>
                      Error details
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {messages.map((row, index) => {
                    const recordId = getCampaignFunnelRecordId(row);
                    const statusColor =
                      row.status === "FAILED"
                        ? theme.palette.error.main
                        : row.status === "READ"
                          ? theme.palette.success.main
                          : row.status === "DELIVERED"
                            ? theme.palette.success.dark
                          : theme.palette.primary.main;

                    return (
                      <TableRow
                        key={row._id}
                        hover
                        sx={{
                          "& td": {
                            borderColor: alpha(theme.palette.text.primary, 0.08),
                            py: 1.5,
                            verticalAlign: "top",
                          },
                          bgcolor: index % 2 === 0 ? "#FFFFFF" : "#FBFDFF",
                        }}
                      >
                        <TableCell sx={{ minWidth: 160 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {row.contactName || row.to}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.65 }}>
                            {row.contactName ? row.to : "Lead source number"}
                          </Typography>
                          {row.listName && (
                            <Typography
                              variant="caption"
                              sx={{ display: "block", opacity: 0.65 }}
                            >
                              List: {row.listName}
                            </Typography>
                          )}
                        </TableCell>

                        {(campaign?.deliveryBatches?.length || 0) > 0 && (
                          <TableCell sx={{ minWidth: 90 }}>
                            {row.batchNumber ? `Batch ${row.batchNumber}` : "-"}
                          </TableCell>
                        )}

                        <TableCell sx={{ minWidth: 120 }}>
                          <Chip
                            size="small"
                            label={row.status}
                            sx={{
                              color: statusColor,
                              bgcolor: alpha(statusColor, 0.1),
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ minWidth: 210 }}>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={recordStageLookup[recordId] || ""}
                            disabled={funnelLoading}
                            helperText="Move this lead into your CRM pipeline"
                            onChange={(event) => {
                              const nextStageId = event.target.value;

                              if (!nextStageId) {
                                updateFunnelState((current) =>
                                  removeCampaignFunnelRecord(current, recordId),
                                );
                                return;
                              }

                              updateFunnelState((current) =>
                                assignCampaignLeadToStage(current, row, nextStageId),
                              );
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2.5,
                                bgcolor: "#FFFFFF",
                              },
                            }}
                          >
                            <MenuItem value="">Not in CRM yet</MenuItem>
                            {funnelState.stages.map((stage) => (
                              <MenuItem key={stage.id} value={stage.id}>
                                {stage.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 240,
                            wordBreak: "break-word",
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: "#334155",
                          }}
                        >
                          {row.waMessageId || "-"}
                        </TableCell>

                        <TableCell sx={{ minWidth: 260, color: "#475569" }}>
                          {getCampaignMessageErrorText(row)}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {messages.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={(campaign?.deliveryBatches?.length || 0) > 0 ? 6 : 5} sx={{ py: 5, textAlign: "center" }}>
                        <Typography variant="body2" sx={{ opacity: 0.72 }}>
                          No message activity found for this filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      ) : (
        <Box>
          <CampaignFunnelPanel
            campaignName={campaign?.name || ""}
            totalLeads={totalCampaignLeads}
            funnelState={funnelState}
            disabled={funnelLoading}
            onAddStage={handleAddStage}
            onChangeRecordStage={(recordId, stageId) =>
              updateFunnelState((current) =>
                moveCampaignFunnelRecord(current, recordId, stageId),
              )
            }
            onRemoveRecord={(recordId) =>
              updateFunnelState((current) =>
                removeCampaignFunnelRecord(current, recordId),
              )
            }
            onUpdateRecord={(recordId, patch) =>
              updateFunnelState((current) =>
                updateCampaignFunnelRecord(current, recordId, patch),
              )
            }
            onCreateCustomField={createCampaignFunnelCustomField}
          />
        </Box>
      )}
    </Box>
  );
}
